import type { FloorsheetPeriod } from '@/features/company/lib/floorsheetPeriod'
import type { InsightPage } from '@/schemas/company-insights'
import type { FinancialBlock, MetricsQuery, PanelQuery, PanelTab } from '@/schemas/company-profile'

/**
 * Query key factory.
 *
 * Keys are built here rather than inline so invalidation can be expressed at
 * the right granularity: invalidating `companyKeys.all` after a market refresh
 * clears every company query, while `companyKeys.detail(symbol)` clears one.
 * Inline array literals scattered across components make that impossible to
 * do correctly.
 *
 * Every per-company read nests under `detail(symbol)` — the overview's own key
 * — so invalidating one company reaches all of its tabs. One branch per
 * response shape, the rule `features/brokers/api/keys.ts` states. The paged
 * lists have TWO branches each, a page and a feed: a feed's cache entry is
 * an `InfiniteData` object, and a plain query landing on that key would read
 * a shape it does not recognise (docs/route-loading.md, "Infinite queries").
 *
 * Query objects arrive here already normalised by their `queryOptions`
 * factory (`profileQueries.ts`) — `compare` spelled out, no `undefined`
 * members — so one request is always one key.
 */
export const companyKeys = {
  all: ['company'] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: (filters: CompanyListFilters) => [...companyKeys.lists(), filters] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (symbol: string) => [...companyKeys.details(), symbol] as const,
  floorsheet: (symbol: string, page: number, pageSize: number) =>
    [...companyKeys.detail(symbol), 'floorsheet', { page, pageSize }] as const,
  largestTrades: (symbol: string, count: number) =>
    [...companyKeys.detail(symbol), 'largest-trades', { count }] as const,

  // ── The Stock page's tabs (`/api/v5/company/{symbol}/…`) ───────────────────

  sentiment: (symbol: string) => [...companyKeys.detail(symbol), 'sentiment'] as const,
  metrics: (symbol: string, query: MetricsQuery) =>
    [...companyKeys.detail(symbol), 'metrics', query] as const,
  panel: (symbol: string, tab: PanelTab, query: PanelQuery) =>
    [...companyKeys.detail(symbol), 'panel', tab, query] as const,
  /** One Financials block (`/financials/{block}/`) at one period, or the latest. */
  financialBlock: (symbol: string, block: FinancialBlock, query: PanelQuery) =>
    [...companyKeys.detail(symbol), 'financial-block', block, query] as const,
  /** Overview's Key numbers (`/key-numbers/`), always the latest filed period. */
  keyNumbers: (symbol: string) => [...companyKeys.detail(symbol), 'key-numbers'] as const,
  technicals: (symbol: string) => [...companyKeys.detail(symbol), 'technicals'] as const,
  returns: (symbol: string) => [...companyKeys.detail(symbol), 'returns'] as const,
  indices: (symbol: string) => [...companyKeys.detail(symbol), 'indices'] as const,
  volatility: (symbol: string) => [...companyKeys.detail(symbol), 'volatility'] as const,
  /** `/company/{symbol}/floorsheet/` — not `floorsheet` above, the market log's pages. */
  floorsheetSummary: (symbol: string, period: FloorsheetPeriod, brokers: number) =>
    [...companyKeys.detail(symbol), 'floorsheet-summary', { period, brokers }] as const,
  dividends: (symbol: string) => [...companyKeys.detail(symbol), 'dividends'] as const,
  events: (symbol: string) => [...companyKeys.detail(symbol), 'events'] as const,
  agms: (symbol: string, page: number, pageSize: number) =>
    [...companyKeys.detail(symbol), 'agms', { page, pageSize }] as const,
  agmsFeed: (symbol: string, pageSize: number) =>
    [...companyKeys.detail(symbol), 'agms-feed', { pageSize }] as const,
  announcements: (symbol: string, page: number, pageSize: number) =>
    [...companyKeys.detail(symbol), 'announcements', { page, pageSize }] as const,
  announcementsFeed: (symbol: string, pageSize: number) =>
    [...companyKeys.detail(symbol), 'announcements-feed', { pageSize }] as const,
  shareholding: (symbol: string) => [...companyKeys.detail(symbol), 'shareholding'] as const,
  boardOfDirectors: (symbol: string) =>
    [...companyKeys.detail(symbol), 'board-of-directors'] as const,
  swot: (symbol: string) => [...companyKeys.detail(symbol), 'swot'] as const,
  annualReports: (symbol: string) => [...companyKeys.detail(symbol), 'annual-reports'] as const,
  /** Mitra's take on one page (`/company/{symbol}/insights/{page}/`). */
  insight: (symbol: string, page: InsightPage) =>
    [...companyKeys.detail(symbol), 'insight', page] as const,
  /**
   * The same take with its explainer (`part=full`). A sibling branch, not
   * nested under `insight`: one branch per response shape, and a card read's
   * explainer is always null.
   */
  insightFull: (symbol: string, page: InsightPage) =>
    [...companyKeys.detail(symbol), 'insight-full', page] as const,
  /** Not under one company: the same comparison is every listed company's. */
  compare: (symbols: readonly string[]) => [...companyKeys.all, 'compare', symbols] as const,
}

export interface CompanyListFilters {
  search?: string | undefined
  sector?: string | undefined
}
